import { appPaths } from "../../Routes";
import { type CompanyStatusFilters } from "../../models/api/admin/company";
import { type DriverCategory } from "../../utils/driverPageLocation";
import { useNavigate } from "react-router-dom";
import { type FilterType } from "../../models/app/admin/filter";
import { useCompanyDepartmentSelector } from "../ReusableComponents/CompanyDepartmentSelector";
import {
    type CompanyChecksThisMonthDto,
    type CompanyUpcomingChecksDto,
    type CompanyDeclarationsQueryDto,
    type CompanyDriversDto,
    type CompanyGreyFleetDriversDto,
} from "../../models/app/admin/companyDashboard";
import { successToast } from "../../toast";
import { DeclarationTableFilters } from "../../api/admin/eDeclarations";
import {
    declarationTableFiltersDefaultValues,
    useDeclarationTableFiltersLocalStorage,
} from "../../hooks/useDeclarationTableFiltersLocalStorage";
import {
    DeclarationType,
    EDeclarationsDeclarationQueryParams,
} from "../../models/app/eDeclarations";
import { driverListFiltersDefaultValues } from "../DriverListFilters/DriversListFiltersForm/driversListFiltersDefaultValues";
import { DriversListFiltersFormValues } from "../DriverListFilters/DriversListFiltersForm";
import useTableStore from "../../stores/useTableStore";

export const useCompanyDashboardActions = (
    setStatusFilter?: (value: CompanyStatusFilters | null) => void,
    tableRef?: React.MutableRefObject<HTMLDivElement | null>,
    id?: string,
    componentRef?: string,
    handleFetchDrivers?: (
        filters?: DriversListFiltersFormValues | undefined,
        filterId?: string | undefined
    ) => Promise<boolean>
) => {
    const navigate = useNavigate();
    const setAppliedFilter = useTableStore((state) => state.setAppliedFilter);
    const driverDefaultVals = driverListFiltersDefaultValues("drivers");
    const greyfleetDefaultVals = driverListFiltersDefaultValues("greyFleet");

    const allCompaniesStatsActions = (status: CompanyStatusFilters) => {
        if (!setStatusFilter) return;
        setStatusFilter(status);
        successToast("Set status filter.");
        if (tableRef?.current) {
            window.scrollTo(0, tableRef.current.offsetTop);
        }
    };

    const { handleResetSelectedCompanyDepartment } =
        useCompanyDepartmentSelector();

    const setScrollToTable = useTableStore((state) => state.setScrollToTable);

    const handleApplyDriverFilter = (
        filter: FilterType,
        location: DriverCategory
    ) => {
        handleResetSelectedCompanyDepartment();
        if (location === "drivers") {
            // For allDrivers page
            if (componentRef === "driversPageContent") {
                if (handleFetchDrivers) {
                    setAppliedFilter(filter, location);
                    handleFetchDrivers(filter.filterFields, filter.filterId);
                }
            } else {
                setAppliedFilter(filter, location);
                setScrollToTable(true);
                navigate(appPaths.adminDrivers);
            }
        }
        if (location === "greyfleet") {
            setAppliedFilter(filter, location);
            setScrollToTable(true);
            navigate(appPaths.adminGreyFleet);
        }
    };

    const { setDeclarationTableFiltersStore } =
        useDeclarationTableFiltersLocalStorage();

    const handleApplyDeclarationsFilter = (filter: DeclarationTableFilters) => {
        handleResetSelectedCompanyDepartment();
        setDeclarationTableFiltersStore(filter);
        setScrollToTable(true);
        navigate(appPaths.adminDeclarations);
    };

    const companyDriversStatsActions = (key: keyof CompanyDriversDto) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...driverDefaultVals,
                company: {
                    // reset any applied company for other pages,
                    // as we don't want them to affect the filter
                    ...(id && { companyIds: [id] }),
                    companyDashboard: {
                        companyDrivers: {
                            [key]: true,
                        },
                    },
                },
            },
        };
        handleApplyDriverFilter(filter, "drivers");
    };

    const companyGreyfleetDriversStatsActions = (
        key: keyof CompanyGreyFleetDriversDto
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...greyfleetDefaultVals,
                company: {
                    ...(id && { companyIds: [id] }),
                    companyDashboard: {
                        companyGreyfleetDrivers: {
                            [key]: true,
                        },
                    },
                },
            },
        };
        handleApplyDriverFilter(filter, "greyfleet");
    };

    const declarationsQueryDefaultValues: EDeclarationsDeclarationQueryParams =
        {
            declarationTypes: null,
            hasExpiredDeclaration: null,
            hasCompletedDeclaration: null,
            hasIncompleteDeclaration: null,
            hasExpiringDeclaration: null,
        };

    const companyDeclarationsQueryStatsActions = (
        key: keyof CompanyDeclarationsQueryDto
    ) => {
        switch (key) {
            case "withDeclarations":
                handleApplyDeclarationsFilter(
                    declarationTableFiltersDefaultValues
                );
                break;
            case "completedDeclarations":
                handleApplyDeclarationsFilter({
                    ...declarationTableFiltersDefaultValues,
                    declarationsQuery: {
                        ...declarationsQueryDefaultValues,
                        declarationTypes: [
                            DeclarationType.EDeclaration,
                            DeclarationType.PaperDeclaration,
                        ],
                        hasCompletedDeclaration: true,
                    },
                });
                break;
            case "expiringDeclarations":
                handleApplyDeclarationsFilter({
                    ...declarationTableFiltersDefaultValues,
                    declarationsQuery: {
                        ...declarationsQueryDefaultValues,
                        declarationTypes: [
                            DeclarationType.EDeclaration,
                            DeclarationType.PaperDeclaration,
                        ],
                        hasExpiringDeclaration: true,
                    },
                });
                break;
            case "declarationsExpiredOrNotReturned":
                handleApplyDeclarationsFilter({
                    ...declarationTableFiltersDefaultValues,
                    declarationsQuery: {
                        ...declarationsQueryDefaultValues,
                        declarationTypes: [
                            DeclarationType.EDeclaration,
                            DeclarationType.PaperDeclaration,
                        ],
                        hasIncompleteDeclaration: true,
                        hasExpiredDeclaration: true,
                    },
                });
                break;
        }
    };

    const companyChecksThisMonthStatsActions = (
        key: keyof CompanyChecksThisMonthDto
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...driverDefaultVals,
                company: {
                    ...(id && { companyIds: [id] }),
                    companyDashboard: {
                        companyChecksThisMonth: {
                            [key]: true,
                        },
                    },
                },
            },
        };
        handleApplyDriverFilter(filter, "drivers");
    };

    const companyUpcomingChecksStatsActions = (
        key: keyof CompanyUpcomingChecksDto
    ) => {
        const filter: FilterType = {
            filterId: "",
            filterFields: {
                ...driverDefaultVals,
                company: {
                    ...(id && { companyIds: [id] }),
                    companyDashboard: {
                        companyUpcomingChecks: {
                            [key]: true,
                        },
                    },
                },
            },
        };
        handleApplyDriverFilter(filter, "drivers");
    };

    return {
        allCompaniesStatsActions,
        companyDriversStatsActions,
        companyGreyfleetDriversStatsActions,
        companyDeclarationsQueryStatsActions,
        companyChecksThisMonthStatsActions,
        companyUpcomingChecksStatsActions,
    };
};
